import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { HidePasswordIcon, ShowPasswordIcon } from '@/components';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextFieldType } from '@/type';
import { buildFormFieldLabel } from '@/util';
import { StringSchema } from 'yup';

interface PasswordFieldProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  textField: TextFieldType;
}

const PasswordFieldController = <TFieldValues extends FieldValues = FieldValues>({
  control,
  textField,
}: PasswordFieldProps<TFieldValues>) => {
  const { t } = useTranslation();
  const [isShowPassword, setIsShowPassword] = useState(false);

  const handleShowPasswordMouseDown = () => setIsShowPassword(true);
  const handleShowPasswordMouseUp = () => setIsShowPassword(false);

  return (
    <Controller
      name={textField.name as Path<TFieldValues>}
      control={control}
      render={({ field, fieldState }) => {
        const { error } = fieldState;

        return (
          <TextField
            {...field}
            id='password-input'
            label={buildFormFieldLabel(
              t(textField.label),
              textField.validation as StringSchema,
            )}
            type={isShowPassword ? 'text' : 'password'}
            fullWidth
            variant='outlined'
            disabled={textField.disabled}
            error={!!error}
            helperText={error ? t(error.message || '') : ''}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position='end'
                  onMouseDown={handleShowPasswordMouseDown}
                  onMouseUp={handleShowPasswordMouseUp}
                  style={{ cursor: 'pointer' }}
                >
                  {isShowPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                </InputAdornment>
              ),
              startAdornment: textField.preFix ? (
                <InputAdornment position='start'>{textField.preFix}</InputAdornment>
              ) : null,
            }}
            InputLabelProps={{
              shrink: Boolean(field.value),
            }}
          />
        );
      }}
    />
  );
};

export default PasswordFieldController;
